import { Check, Trash2, Ban } from 'lucide-react';
import { StagePill } from '../workflow/StagePill';
import { ChannelDots } from '../workflow/ChannelChips';
import { productTypeEmoji } from '../../lib/workflow';

const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '');

const SelectBox = ({ id, selected, onToggle, className = '' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onToggle(id); }}
    className={`z-10 w-6 h-6 rounded-lg border flex items-center justify-center transition-colors ${selected ? 'bg-[#2D2623] border-[#2D2623] text-white' : 'bg-white/90 border-[#D8CBB7] text-transparent hover:border-[#6E635B]'} ${className}`}
    data-testid={`etsy-select-${id}`}
    aria-label="Select listing"
  >
    <Check size={14} />
  </button>
);

export const ListingCard = ({ p, index, selected, onToggle, onOpen, onDelete }) => (
  <div
    onClick={() => onOpen(p.id)}
    className={`group wf-card wf-card-hover wf-rise overflow-hidden flex flex-col cursor-pointer rounded-2xl ${selected ? 'ring-2 ring-[#2D2623]' : p.not_for_etsy ? 'border-amber-300' : ''}`}
    style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
    data-testid={`etsy-product-card-${p.id}`}
  >
    <div className="relative aspect-[4/3] bg-[#F7F3EC] overflow-hidden" data-testid={`etsy-product-thumb-${p.id}`}>
      <SelectBox id={p.id} selected={selected} onToggle={onToggle} className="absolute top-2 left-2" />
      {p.thumbnail_url ? (
        <img src={p.thumbnail_url} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-4xl opacity-60" aria-hidden>{productTypeEmoji(p.product_type)}</div>
      )}
      {p.stage && <div className="absolute top-2 right-2"><StagePill stage={p.stage} size="xs" testId={`etsy-stage-pill-${p.id}`} /></div>}
      <div className="absolute bottom-2 left-2 flex gap-1">
        {p.has_video && <span className="rounded-full bg-black/70 text-white text-[10px] font-bold px-2 py-0.5" data-testid={`etsy-video-badge-${p.id}`}>🎬 Video</span>}
        {p.media_count > 0 && <span className="rounded-full bg-white/90 text-[#2D2623] text-[10px] font-bold px-2 py-0.5">📸 {p.media_count}</span>}
      </div>
    </div>
    <div className="p-3 flex flex-col gap-2 flex-1">
      <h3 className="text-[13px] font-bold text-[#2D2623] line-clamp-2 leading-snug">{p.status === 'processing' ? '✨ Generating listing…' : p.name}</h3>
      <div className="flex flex-wrap items-center gap-1">
        {p.shape && <span className="text-[10px] font-semibold uppercase tracking-wide text-[#6E635B] bg-[#F7F3EC] rounded-full px-2 py-0.5" data-testid={`etsy-shape-chip-${p.id}`}>{p.shape}</span>}
        {p.not_for_etsy && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`etsy-not-for-etsy-tag-${p.id}`}><Ban size={9} /> Not for Etsy</span>}
      </div>
      {p.stage && ['live_on_etsy', 'done'].includes(p.stage) && <ChannelDots channels={p.channels} testId={`etsy-channel-dots-${p.id}`} />}
      <div className="flex items-center justify-between text-[11px] text-[#9A8E85] mt-auto pt-2 border-t border-[#F0EAE1]">
        <span className="truncate">👤 {p.workflow?.submitted_by_name || p.created_by_name || '—'} · {timeOf(p.created_at)}</span>
        <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} className="text-[#9A8E85] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-etsy-product-${p.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  </div>
);

export const ListingRow = ({ p, selected, onToggle, onOpen, onDelete }) => (
  <div
    onClick={() => onOpen(p.id)}
    className={`group flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-[#FDFBF7] ${selected ? 'bg-[#F7F3EC]' : ''}`}
    data-testid={`etsy-product-row-${p.id}`}
  >
    <SelectBox id={p.id} selected={selected} onToggle={onToggle} />
    <div className="w-11 h-11 rounded-xl overflow-hidden bg-[#F7F3EC] shrink-0 flex items-center justify-center text-xl">
      {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" /> : <span aria-hidden>{productTypeEmoji(p.product_type)}</span>}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-[#2D2623] truncate">{p.status === 'processing' ? '✨ Generating listing…' : p.name}</p>
      <p className="text-[11px] text-[#9A8E85] truncate">
        {p.shape ? `${p.shape} · ` : ''}📸 {p.media_count || 0}{p.has_video ? ' · 🎬' : ''}{p.not_for_etsy ? ' · 🚫 Not for Etsy' : ''}
      </p>
    </div>
    <div className="hidden md:block">{p.stage && ['live_on_etsy', 'done'].includes(p.stage) && <ChannelDots channels={p.channels} />}</div>
    {p.stage ? <StagePill stage={p.stage} size="xs" /> : <span className="text-[10px] text-[#9A8E85]">—</span>}
    <span className="hidden lg:block w-28 truncate text-[11px] text-[#9A8E85]">👤 {p.workflow?.submitted_by_name || p.created_by_name || '—'}</span>
    <span className="w-16 text-right text-[11px] text-[#9A8E85]">{timeOf(p.created_at)}</span>
    <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} className="text-[#9A8E85] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-etsy-product-${p.id}`}>
      <Trash2 size={13} />
    </button>
  </div>
);
